/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {addSlider, createCheckbox, paramExists, ThemedPanel} from "./ThemedPanel";
import {createSunLight} from "@luciad/ria/view/LightEffect";
import {LightScatteringAtmosphere} from "@luciad/ria/view/LightScatteringAtmosphere";

const THEME_SUNLIGHT = 11;
const THEME_ATMOSPHERE = 12;
const THEME_AMBIENT = 13;

const SLIDER_OPTIONS_LIGHTNING_HOUR = {
  label: "Čas:",
  value: "16.0",
  min: "0.5",
  max: "24.0",
  step: "" + 1 / 60,
  precision: "2",
  valueFormatter: function(value) {
    const hr = Math.floor(+value);
    const minutes = Math.round((+value % 1) * 60);
    return hr + ":" + (minutes > 9 ? minutes : "0" + minutes);
  }
};

const SLIDER_OPTIONS_ATMOSPHERE_BRIGHTNESS = {
  label: "Jas:",
  value: "2.0",
  min: "0.5",
  max: "6.0",
  step: "0.05",
  precision: "2"
};

const SLIDER_OPTIONS_ATMOSPHERE_RAYLEIGH_SCATTERING = {
  label: "Hustota:",
  value: "1.0",
  min: "0.05",
  max: "5.0",
  step: "0.05",
  precision: "2"
};

const SLIDER_OPTIONS_AMBIENT_OCCLUSION_RADIUS = {
  label: "Poloměr:",
  value: "30",
  min: "0.5",
  max: "50",
  step: "0.5"
};

const SLIDER_OPTIONS_AMBIENT_OCCLUSION_POWER = {
  label: "Intenzita:",
  value: "1.2",
  min: "0.05",
  max: "5.0",
  step: "0.05"
};

const THEMES = [{
  id: THEME_SUNLIGHT,
  text: "Sluneční svit",
  container: "lighting",
  only3D: true,
  init: (panelDomNode, themeControl, map) => {
    const hourComponent = addSlider(panelDomNode, SLIDER_OPTIONS_LIGHTNING_HOUR);
    const shadowComponent = createCheckbox(panelDomNode, "Stíny", true);

    const onChange = () => {
      const active = themeControl.isActive();
      if (active) {
        applyLighting(map, hourComponent.value, shadowComponent.checked);
      } else {
        applyLighting(map, null);
      }
      hourComponent.disabled = !active;
      shadowComponent.disabled = !active;
    }

    hourComponent.addHandler(onChange);
    shadowComponent.addEventListener('change', onChange);

    return onChange;
  }
}, {
  id: THEME_ATMOSPHERE,
  text: "Obloha",
  container: "atmosphere",
  only3D: true,
  init: (panelDomNode, themeControl, map) => {
    const brightnessComponent = addSlider(panelDomNode, SLIDER_OPTIONS_ATMOSPHERE_BRIGHTNESS);
    const rayleighScatteringComponent = addSlider(panelDomNode, SLIDER_OPTIONS_ATMOSPHERE_RAYLEIGH_SCATTERING);

    const onChange = () => {
      const active = themeControl.isActive();
      if (active) {
        map.effects.atmosphere = new LightScatteringAtmosphere({
          brightness: brightnessComponent.value,
          rayleighScatteringFactor: rayleighScatteringComponent.value
        });
      } else {
        map.effects.atmosphere = true;
      }
      brightnessComponent.disabled = !active;
      rayleighScatteringComponent.disabled = !active;
    }

    brightnessComponent.addHandler(onChange);
    rayleighScatteringComponent.addHandler(onChange);

    return onChange;
  }
}, {
  id: THEME_AMBIENT,
  text: "Nepřímé světlo",
  container: "ambient",
  only3D: true,
  init: (panelDomNode, themeControl, map) => {
    const aoRadiusComponent = addSlider(panelDomNode, SLIDER_OPTIONS_AMBIENT_OCCLUSION_RADIUS);
    const aoPowerComponent = addSlider(panelDomNode, SLIDER_OPTIONS_AMBIENT_OCCLUSION_POWER);

    const onChange = () => {
      const active = themeControl.isActive();
      if (active) {
        applyOcclusion(map, aoRadiusComponent.value, aoPowerComponent.value);
      } else {
        applyOcclusion(map, null);
      }
      aoRadiusComponent.disabled = !active;
      aoPowerComponent.disabled = !active;
    }

    aoRadiusComponent.addHandler(onChange);
    aoPowerComponent.addHandler(onChange);

    return onChange;
  }
}];

function applyLighting(map, timeOfDay, withShadows) {
  if (paramExists(timeOfDay)) {
    const time = new Date(Date.UTC(2019, 6, 21, Math.floor(timeOfDay), (timeOfDay - Math.floor(timeOfDay)) * 60, 0, 0));
    map.effects.light = createSunLight({
      time: time,
      shadows: !!withShadows
    });
  } else {
    map.effects.light = null;
  }
}

function applyOcclusion(map, aoRadius, aoPower) {
  if (paramExists(aoRadius) && paramExists(aoPower)) {
    map.effects.ambientOcclusion = {
      radius: aoRadius,
      power: aoPower
    };
  } else {
    map.effects.ambientOcclusion = null;
  }
}

export class LightPanel extends ThemedPanel {
  constructor(map, options) {
    options = options || {};
    options.title = options.title || "OSVĚTLENÍ";
    options.icon = options.icon || "lcdIconSun";
    super(map, options, THEMES);
    this.init(map, options);
  }
}